import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/admin/_layout/inventory/customer-returns")({
  component: CustomerReturnsPage,
});

import { useState } from "react";
import { toast } from "sonner";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import { SelectFilter } from "@/components/crud/filters/SelectFilter";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import {
  useFetchCustomerReturns,
  useCustomerReturnMutations,
} from "@/hooks/api/inventory/useStockOps";
import {
  useFetchWarehouses,
  useFetchItems,
  useFetchUnits,
  ALL_ACTIVE_PARAMS,
} from "@/hooks/api/inventory/useCatalog";
import { CustomerReturnTable } from "@/components/inventory/tables/CustomerReturnTable";
import { CustomerReturnFormSheet } from "@/components/inventory/forms/CustomerReturnFormSheet";
import { CustomerReturnDetailSheet } from "@/components/inventory/forms/DetailViewSheet";
import type { CustomerReturnPayload, CustomerReturn } from "@/types/inventory";

function CustomerReturnsPage() {
  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();
  const [showFilters, setShowFilters] = useState(false);
  const [warehouseFilter, setWarehouseFilter] = useState<string | undefined>(
    undefined
  );
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [viewTarget, setViewTarget] = useState<CustomerReturn | null>(null);

  const customerReturnsQuery = useFetchCustomerReturns({
    page_size: pageSize,
    page,
    warehouse: warehouseFilter ? Number(warehouseFilter) : undefined,
  });
  const customerReturns = customerReturnsQuery.data?.results ?? [];
  const count = customerReturnsQuery.data?.count ?? 0;

  const { createCustomerReturn } = useCustomerReturnMutations();
  const warehousesQuery = useFetchWarehouses(ALL_ACTIVE_PARAMS);
  const warehouses = warehousesQuery.data?.results ?? [];
  const itemsQuery = useFetchItems(ALL_ACTIVE_PARAMS);
  const items = itemsQuery.data?.results ?? [];
  const unitsQuery = useFetchUnits(ALL_ACTIVE_PARAMS);
  const units = unitsQuery.data?.results ?? [];
  const isMutating = createCustomerReturn.isPending;
  const isLoading = customerReturnsQuery.isPending || isMutating;
  const isFetching = customerReturnsQuery.isFetching || isMutating;

  const resetFilters = () => {
    setWarehouseFilter(undefined);
    setPage(1);
  };

  const handleSubmit = async (data: CustomerReturnPayload) => {
    await createCustomerReturn.mutateAsync(data);
    toast.success("Customer return recorded");
    setIsSheetOpen(false);
  };

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Customer Returns"
          description="Record stock returned by customers after sale"
          onToggleFilter={() => setShowFilters((prev) => !prev)}
          onAdd={() => setIsSheetOpen(true)}
          addLabel="Record Customer Return"
          isLoading={isFetching}
        />

        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          <SelectFilter
            placeholder="Warehouse"
            options={warehouses.map((w) => ({
              label: w.name,
              value: String(w.id),
            }))}
            value={warehouseFilter}
            onChange={(v) => {
              setWarehouseFilter(v);
              setPage(1);
            }}
          />
        </FiltersPanel>

        <CustomerReturnTable
          customerReturns={customerReturns}
          isLoading={isLoading}
          onView={(r) => setViewTarget(r)}
        />
        <PaginationComponent
          count={count}
          page={page}
          onPageChange={(p) => setPage(p)}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />

        <CustomerReturnFormSheet
          isOpen={isSheetOpen}
          onClose={() => setIsSheetOpen(false)}
          onSubmit={handleSubmit}
          warehouses={warehouses}
          items={items}
          units={units}
          isLoading={isMutating}
        />

        {viewTarget && (
          <CustomerReturnDetailSheet
            isOpen={!!viewTarget}
            onClose={() => setViewTarget(null)}
            record={viewTarget}
          />
        )}
      </div>
    </SidebarInset>
  );
}
